import {
  StatusChip,
  DataTable,
  formatDate as formatAdminDate,
  type Column,
  type RowAction,
} from "@pervasivemind/admin-ui";
import { ArrowUpRight, GripVertical, Trash2 } from "lucide-react";
import { Spinner } from "@pervasivemind/bi-components";
import { formatRelative } from "@pervasivemind/dashboard-render";
import { useState, type HTMLAttributes } from "react";
import { useNavigate } from "react-router-dom";

import { useT } from "@/i18n";
import { findIconComponent } from "@/lib/project-icons";
import {
  resolveLocalized,
  type DisplayLabel,
} from "@pervasivemind/dashboard-render";
import type { ReportDraft } from "@/types";

interface DraftsTableProps {
  drafts: ReportDraft[];
  busyKey: string | null;
  onDelete: (draft: ReportDraft) => void;
  /**
   * Sidebar-order DnD (2026-07-20). When present, rows grow a drag
   * handle; dropping calls back with the FULL key list in the new
   * order (the hub persists optimistically and reverts on failure).
   * Absent for the "_unassigned" pseudo-project and 1-row projects.
   */
  onReorder?: (orderedKeys: string[]) => void;
}

/**
 * Report rows of a project.
 *
 * Renders through ``DataTable`` from ``@pervasivemind/admin-ui`` — the
 * same table the rest of the two admin apps use — rather than the
 * hand-rolled ``<table>`` it carried since the PR-0 builder reorg. That
 * is what makes the header, the row height and the action grammar match
 * every other list; the drag-and-drop rows survive through the table's
 * ``rowProps`` / ``leadingCell`` hooks, which exist for exactly this.
 *
 * Drag & drop: HTML5 native (no dependency) — the handle ARMS the row
 * (``armedKey``) on mousedown so only handle-initiated drags start (the
 * row also hosts Edit/Delete). While dragging, the source row dims and
 * the hovered row highlights; the drop position (before / after the
 * hovered row) follows the pointer's half of the row.
 */
export function DraftsTable({
  drafts,
  busyKey,
  onDelete,
  onReorder,
}: DraftsTableProps): JSX.Element {
  const { t, locale } = useT();
  const navigate = useNavigate();

  const [armedKey, setArmedKey] = useState<string | null>(null);
  const [dragKey, setDragKey] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<{
    key: string;
    after: boolean;
  } | null>(null);

  const clearDrag = (): void => {
    setArmedKey(null);
    setDragKey(null);
    setDropTarget(null);
  };

  const handleDrop = (): void => {
    if (!onReorder || !dragKey || !dropTarget || dropTarget.key === dragKey) {
      clearDrag();
      return;
    }
    const without = drafts.map((d) => d.key).filter((k) => k !== dragKey);
    const anchor = without.indexOf(dropTarget.key);
    const at = anchor + (dropTarget.after ? 1 : 0);
    const next = [...without.slice(0, at), dragKey, ...without.slice(at)];
    const unchanged = next.every((k, i) => drafts[i]?.key === k);
    if (!unchanged) onReorder(next);
    clearDrag();
  };

  const columns: Column<ReportDraft>[] = [
    {
      id: "key",
      header: t("list.col.key"),
      cell: (draft) => (
        <span className="font-mono text-xs text-neutral-600">{draft.key}</span>
      ),
    },
    {
      id: "name",
      header: t("list.col.name"),
      cell: (draft) => {
        // display_name may be a bilingual ``{en, es}`` dict — resolve with
        // the admin's active UI locale.
        const displayName =
          resolveLocalized(
            draft.report_config?.display_name as DisplayLabel,
            locale,
          ) || draft.key;
        // PR-1: per-report icon (``report_config.icon`` — the portal
        // renders this same string).
        const ReportIcon = findIconComponent(draft.report_config?.icon);
        return (
          <span className="inline-flex items-center gap-2">
            {ReportIcon && (
              <ReportIcon size={15} className="shrink-0 text-primary" aria-hidden />
            )}
            {displayName}
          </span>
        );
      },
    },
    {
      // Report groups: the group this report is one tab of, linked to
      // the group's page (the table filter × report).
      id: "group",
      header: t("reportGroup.column"),
      cell: (draft) =>
        draft.group && draft.project_slug ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              navigate(
                `/projects/${encodeURIComponent(draft.project_slug ?? "")}/groups/${encodeURIComponent(draft.group!.key)}`,
              );
            }}
            className="inline-flex items-center gap-1 rounded-md border border-primary/30 bg-primary-soft px-2 py-0.5 text-xs font-semibold text-primary hover:bg-primary-line"
            data-report-group={draft.group.key}
          >
            {resolveLocalized(draft.group.display_name as DisplayLabel, locale) || draft.group.key}
            {draft.group.position !== null && ` · ${draft.group.position + 1}`}
          </button>
        ) : (
          <span className="text-neutral-500">—</span>
        ),
    },
    {
      id: "status",
      header: t("list.col.status"),
      cell: (draft) =>
        draft.status === "published" ? (
          <StatusChip label={t("list.status.published")} tone="success" />
        ) : (
          <StatusChip label={t("list.status.draft")} tone="neutral" />
        ),
    },
    {
      id: "updated",
      header: t("list.col.updated"),
      // Absolute in the cell, relative on hover. ``formatRelative`` flips
      // to an absolute date past 30 days, which made this column read as
      // two formats at once — see docs/ui-audit/2026-09 §B4.
      // Aliased on import: `@pervasivemind/dashboard-render` exports a
      // DIFFERENT `formatDate`, and an unqualified name would let an
      // auto-import pick the wrong one silently.
      cell: (draft) => {
        const { text, invalid } = formatAdminDate(draft.updated_at, {
          locale,
          mode: "absolute",
        });
        return (
          <span
            className={invalid ? "font-mono text-amber-800" : "text-neutral-600"}
            title={
              invalid
                ? t("common.missingLabel")
                : formatRelative(draft.updated_at)
            }
          >
            {text}
          </span>
        );
      },
    },
  ];

  const actions: RowAction<ReportDraft>[] = [
    {
      id: "edit",
      label: t("common.edit"),
      icon: <ArrowUpRight size={14} />,
      onSelect: (draft) => navigate(`/builder/${encodeURIComponent(draft.key)}`),
    },
    {
      id: "delete",
      label: t("common.delete"),
      icon: (draft) =>
        busyKey === draft.key ? (
          <Spinner size={14} color="currentColor" ariaLabel={t("common.delete")} />
        ) : (
          <Trash2 size={14} />
        ),
      danger: true,
      disabled: (draft) => busyKey === draft.key,
      onSelect: onDelete,
    },
  ];

  const rowProps = (draft: ReportDraft): HTMLAttributes<HTMLTableRowElement> => {
    if (!onReorder) return {};
    const isDragging = dragKey === draft.key;
    const isDropTarget = dropTarget?.key === draft.key && dragKey !== draft.key;
    return {
      draggable: armedKey === draft.key,
      onDragStart: (e) => {
        e.dataTransfer.effectAllowed = "move";
        // Firefox needs data set for the drag to start.
        e.dataTransfer.setData("text/plain", draft.key);
        setDragKey(draft.key);
      },
      onDragOver: (e) => {
        if (!dragKey) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        const rect = e.currentTarget.getBoundingClientRect();
        const after = e.clientY > rect.top + rect.height / 2;
        setDropTarget((prev) =>
          prev?.key === draft.key && prev.after === after
            ? prev
            : { key: draft.key, after },
        );
      },
      onDrop: (e) => {
        e.preventDefault();
        handleDrop();
      },
      onDragEnd: clearDrag,
      className: `transition-colors ${isDragging ? "opacity-40" : ""} ${
        isDropTarget ? "bg-primary-soft/70" : ""
      }`,
    } as HTMLAttributes<HTMLTableRowElement>;
  };

  return (
    <DataTable
      columns={columns}
      rows={drafts}
      rowKey={(draft) => draft.key}
      actions={actions}
      caption={t("hub.tab.reports")}
      rowProps={onReorder ? rowProps : undefined}
      leadingCell={
        onReorder
          ? (draft) => (
              <span
                role="button"
                tabIndex={-1}
                aria-label={t("list.dragHandle")}
                title={t("list.dragHandle")}
                onMouseDown={() => setArmedKey(draft.key)}
                onMouseUp={() => setArmedKey(null)}
                className="inline-flex cursor-grab touch-none items-center text-neutral-500 hover:text-neutral-600 active:cursor-grabbing"
              >
                <GripVertical size={15} />
              </span>
            )
          : undefined
      }
    />
  );
}
